import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppLink, useGo } from '../components/Nav';
import { ArrowLeftIcon } from '../components/icons';
import { Banner, Button, ErrorText, Loading, Spinner, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { ApiError, errorMessage } from '../lib/api';
import { timecode } from '../lib/format';
import { loadWaveform } from '../player/waveform';
import { applyEdit, fetchEditState, type Range, type Word } from '../queries/edit';

/**
 * The editor.
 *
 * Everything here is relative to the original recording: the video on the
 * left plays the original straight from storage, skipping over cut ranges;
 * the transcript on the right is every word with its time; the timeline
 * along the bottom is the original's length with the cuts drawn on it.
 * "Finish" sends the kept ranges and the corrected words to the API, which
 * renders the new version and regenerates the captions.
 *
 * Nothing is destructive until Finish, and even then the original is kept.
 */

// ---------------------------------------------------------------------------
// Range maths, on the client. Mirrors services/edits.ts on the API.

function complement(keep: Range[], duration: number): Range[] {
  const cuts: Range[] = [];
  let cursor = 0;
  for (const [start, end] of keep) {
    if (start > cursor + 0.001) cuts.push([cursor, start]);
    cursor = Math.max(cursor, end);
  }
  if (cursor < duration - 0.001) cuts.push([cursor, duration]);
  return cuts;
}

function subtract(keep: Range[], cut: Range): Range[] {
  const out: Range[] = [];
  for (const [start, end] of keep) {
    if (cut[1] <= start || cut[0] >= end) {
      out.push([start, end]);
      continue;
    }
    if (cut[0] > start) out.push([start, cut[0]]);
    if (cut[1] < end) out.push([cut[1], end]);
  }
  return out.filter(([s, e]) => e - s >= 0.05);
}

/** Puts a cut back, merging with whatever it touches. */
function restore(keep: Range[], cut: Range): Range[] {
  const merged = [...keep, cut].sort((a, b) => a[0] - b[0]);
  const out: Range[] = [];
  for (const range of merged) {
    const last = out[out.length - 1];
    if (last && range[0] <= last[1] + 0.001) last[1] = Math.max(last[1], range[1]);
    else out.push([range[0], range[1]]);
  }
  return out;
}

/** The kept range at a time, if any. */
function segmentAt(keep: Range[], time: number): Range | null {
  return keep.find(([s, e]) => time >= s && time < e) ?? null;
}


function isCut(time: number, keep: Range[]): boolean {
  return !keep.some(([s, e]) => time >= s && time < e);
}

/** Kept seconds: what the finished video will run. */
function keptDuration(keep: Range[]): number {
  return keep.reduce((total, [s, e]) => total + (e - s), 0);
}

// ---------------------------------------------------------------------------

export function EditPage({ videoId }: { videoId: string }) {
  const go = useGo();
  const state = useQuery({
    queryKey: ['edit', videoId],
    queryFn: () => fetchEditState(videoId),
    retry: 1,
  });

  if (state.isPending) return <Loading label="Opening the editor" />;
  if (state.isError) {
    const upsell = state.error instanceof ApiError && state.error.isQuota;
    return (
      <div className="mx-auto max-w-md py-16">
        {upsell ? (
          <Banner
            tone="info"
            title="Editing comes with a paid seat"
            action={
              <AppLink to="/settings/subscription">
                <Button size="sm" variant="primary">
                  Upgrade
                </Button>
              </AppLink>
            }
          >
            Cut, trim and correct the transcript of anything you record, on the Team plan. The
            recording itself is untouched and still yours.
          </Banner>
        ) : (
          <Banner tone="danger" title="Could not open this recording for editing">
            {errorMessage(state.error)}
          </Banner>
        )}
      </div>
    );
  }
  const data = state.data;
  if (data.status !== 'ready') {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <Spinner size={22} className="mx-auto text-chalk-faint" />
        <h1 className="mt-4 text-h3 text-chalk">Still processing</h1>
        <p className="mt-2 text-body text-chalk-dim">
          Editing opens up once the recording is ready. This usually takes a minute or two.
        </p>
        <AppLink to={`/v/${data.shareId}`} className="mt-5 inline-block">
          <Button>Back to the recording</Button>
        </AppLink>
      </div>
    );
  }
  return <Editor initial={data} onDone={() => go(`/v/${data.shareId}`)} />;
}

function Editor({
  initial,
  onDone,
}: {
  initial: Awaited<ReturnType<typeof fetchEditState>>;
  onDone: () => void;
}) {
  const duration = initial.sourceDurationSec;
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    track('Edit Started', { video_id: initial.shareId });
  }, [initial.shareId]);

  // ---- state and history ---------------------------------------------------
  const [keep, setKeep] = useState<Range[]>(initial.keep ?? [[0, duration]]);
  const [words, setWords] = useState<Word[]>(initial.words ?? []);
  const [history, setHistory] = useState<Array<{ keep: Range[]; words: Word[] }>>([]);
  const [future, setFuture] = useState<Array<{ keep: Range[]; words: Word[] }>>([]);
  const dragBaseline = useRef<{ keep: Range[]; words: Word[] } | null>(null);
  const commit = useCallback(
    (next: { keep?: Range[]; words?: Word[] }) => {
      // An explicit action owns its history entry; a drag in flight does not
      // get to add another at mouse-up.
      dragBaseline.current = null;
      setHistory((h) => [...h, { keep, words }].slice(-100));
      setFuture([]);
      if (next.keep) setKeep(next.keep);
      if (next.words) setWords(next.words);
    },
    [keep, words],
  );
  const undo = () => {
    const last = history[history.length - 1];
    if (!last) return;
    setFuture((f) => [{ keep, words }, ...f]);
    setHistory((h) => h.slice(0, -1));
    setKeep(last.keep);
    setWords(last.words);
  };
  const redo = () => {
    const next = future[0];
    if (!next) return;
    setHistory((h) => [...h, { keep, words }]);
    setFuture((f) => f.slice(1));
    setKeep(next.keep);
    setWords(next.words);
  };
  // Edge drags change `keep` on every move without a history entry; the
  // state before the drag is captured when it starts, and pushed once it
  // ends, so one drag is one undo.
  useEffect(() => {
    const up = () => {
      const base = dragBaseline.current;
      dragBaseline.current = null;
      if (base && JSON.stringify(base.keep) !== JSON.stringify(keep)) {
        setHistory((h) => [...h, base].slice(-100));
        setFuture([]);
      }
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, [keep]);
  const dirty =
    JSON.stringify(keep) !== JSON.stringify(initial.keep ?? [[0, duration]]) ||
    JSON.stringify(words) !== JSON.stringify(initial.words ?? []);

  // ---- playhead and selection ----------------------------------------------
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [selection, setSelection] = useState<Range | null>(null);
  const cuts = useMemo(() => complement(keep, duration), [keep, duration]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTime = () => {
      setTime(video.currentTime);
      // Preview honours the cuts: land in one, jump to the next kept range.
      if (!video.paused && isCut(video.currentTime, keep)) {
        const next = keep.find(([s]) => s > video.currentTime);
        if (next) video.currentTime = next[0];
        else video.pause();
      }
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, [keep]);

  const seek = (at: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, Math.min(at, duration));
    setTime(video.currentTime);
  };
  const toggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play().catch(() => {});
    else video.pause();
  };

  // ---- actions ---------------------------------------------------------------
  const deleteSelection = () => {
    if (!selection) return;
    commit({ keep: subtract(keep, selection) });
    setSelection(null);
  };
  const restoreCut = (cut: Range) => commit({ keep: restore(keep, cut) });
  // Dragging a cut's edge: put the old cut back, take the new one out.
  // Committed on every move; the history is squashed by the mouse-up.
  const moveCutEdge = (cut: Range, edge: 'start' | 'end', to: number) => {
    // First move of a drag: remember where it started, for one undo entry.
    if (!dragBaseline.current) dragBaseline.current = { keep, words };
    const next: Range =
      edge === 'start'
        ? [Math.min(to, cut[1] - 0.05), cut[1]]
        : [cut[0], Math.max(to, cut[0] + 0.05)];
    setKeep(subtract(restore(keep, cut), next));
  };
  const selectSegment = (at: number) => setSelection(segmentAt(keep, at));

  // Keyboard: space plays, delete deletes the selection, cmd/ctrl-z undoes.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')
      )
        return;
      if (event.key === ' ') {
        event.preventDefault();
        toggle();
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        deleteSelection();
      } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // ---- finish ------------------------------------------------------------------
  const finish = useMutation({
    mutationFn: () =>
      applyEdit(initial.videoId, {
        keep: keep.length === 1 && keep[0][0] <= 0 && keep[0][1] >= duration ? null : keep,
        words,
      }),
    onSuccess: () => {
      track('Edit Applied', { video_id: initial.shareId, cuts: complement(keep, duration).length });
      onDone();
    },
  });

  return (
    <div className="flex h-screen flex-col bg-slate-950 text-chalk">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-hairline px-4">
        <AppLink
          to={`/v/${initial.shareId}`}
          aria-label="Back"
          className="text-chalk-dim hover:text-chalk"
        >
          <ArrowLeftIcon size={18} />
        </AppLink>
        <h1 className="min-w-0 flex-1 truncate text-body text-chalk">{initial.title}</h1>
        <span className="text-caption text-chalk-faint">
          {dirty ? 'Unsaved changes' : 'No changes'} · will run {timecode(keptDuration(keep))}
        </span>
        <button
          type="button"
          onClick={undo}
          disabled={history.length === 0}
          className="text-caption text-chalk-dim disabled:opacity-40 hover:text-chalk"
        >
          Undo
        </button>
        <button
          type="button"
          onClick={redo}
          disabled={future.length === 0}
          className="text-caption text-chalk-dim disabled:opacity-40 hover:text-chalk"
        >
          Redo
        </button>
        <Button
          variant="primary"
          size="sm"
          busy={finish.isPending}
          disabled={!dirty}
          onClick={() => finish.mutate()}
        >
          Finish
        </Button>
      </header>

      {finish.isError ? (
        <div className="px-4 pt-3">
          <ErrorText>{errorMessage(finish.error)}</ErrorText>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col bg-black">
          <div className="relative flex min-h-0 flex-1 items-center justify-center">
            <video
              ref={videoRef}
              src={initial.sourceUrl}
              className="max-h-full max-w-full"
              onClick={toggle}
              playsInline
              preload="metadata"
            />
            {isCut(time, keep) ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-950/60">
                <span className="rounded-full bg-slate-900/90 px-3 py-1 text-caption text-chalk">
                  Cut
                </span>
              </div>
            ) : null}
          </div>
          <div className="flex h-11 shrink-0 items-center gap-3 border-t border-hairline px-4 text-caption">
            <button type="button" onClick={toggle} className="text-chalk hover:text-chalk">
              {playing ? 'Pause' : 'Play'}
            </button>
            <span className="font-mono text-chalk-dim">
              {timecode(time)} / {timecode(duration)}
            </span>
            <span className="flex-1" />
            <span className="text-chalk-faint">
              Space plays · Delete removes the selection · ⌘Z undoes
            </span>
          </div>
        </div>

        <aside className="w-[380px] shrink-0 overflow-y-auto border-l border-hairline px-4 py-4">
          <TranscriptEditor
            words={words}
            keep={keep}
            time={time}
            status={initial.transcriptStatus}
            selection={selection}
            onSeek={seek}
            onSelect={setSelection}
            onChangeWord={(index, text) =>
              commit({ words: words.map((w, i) => (i === index ? { ...w, w: text } : w)) })
            }
            onReplaceSpan={(from, to, replacement) =>
              commit({ words: [...words.slice(0, from), ...replacement, ...words.slice(to)] })
            }
          />
        </aside>
      </div>

      <Timeline
        duration={duration}
        cuts={cuts}
        time={time}
        selection={selection}
        sourceUrl={initial.sourceUrl}
        onSeek={seek}
        onSelect={setSelection}
        onDelete={deleteSelection}
        onRestore={restoreCut}
        onMoveCutEdge={moveCutEdge}
        onSelectSegment={selectSegment}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transcript: words in lines. Click a word to go there, drag across words to
// select a range, double-click a word to fix it, or edit a whole line as
// text: its words are re-spread over the line's time, one to one when the
// count matches and evenly when it does not. Cut words are struck through.

interface Line {
  from: number; // index of first word
  to: number; // index after last word
}

/** Lines break at sentence ends, pauses, and about a dozen words. */
function linesOf(words: Word[]): Line[] {
  const lines: Line[] = [];
  let from = 0;
  for (let i = 0; i < words.length; i += 1) {
    const word = words[i];
    const next = words[i + 1];
    const count = i - from + 1;
    const breakHere = !next || count >= 14 || /[.?!]$/.test(word.w) || next.s - word.e > 1.0;
    if (breakHere) {
      lines.push({ from, to: i + 1 });
      from = i + 1;
    }
  }
  return lines;
}

/** New text for a span of words, keeping the span's timing. */
function retime(old: Word[], text: string): Word[] {
  const tokens = text.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];
  if (tokens.length === old.length) return old.map((word, i) => ({ ...word, w: tokens[i] }));
  const start = old[0].s;
  const end = old[old.length - 1].e;
  const each = (end - start) / tokens.length;
  return tokens.map((w, i) => ({ s: start + i * each, e: start + (i + 1) * each, w }));
}

function TranscriptEditor({
  words,
  keep,
  time,
  status,
  selection,
  onSeek,
  onSelect,
  onChangeWord,
  onReplaceSpan,
}: {
  words: Word[];
  keep: Range[];
  time: number;
  status: 'pending' | 'ready' | 'failed' | null;
  selection: Range | null;
  onSeek: (at: number) => void;
  onSelect: (range: Range | null) => void;
  onChangeWord: (index: number, text: string) => void;
  onReplaceSpan: (from: number, to: number, replacement: Word[]) => void;
}) {
  const anchor = useRef<number | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const [editingLine, setEditingLine] = useState<number | null>(null);
  const [lineDraft, setLineDraft] = useState('');
  useEffect(() => {
    const clear = () => {
      anchor.current = null;
    };
    window.addEventListener('mouseup', clear);
    return () => window.removeEventListener('mouseup', clear);
  }, []);
  const lines = useMemo(() => linesOf(words), [words]);

  if (status !== 'ready' || words.length === 0) {
    return (
      <div>
        <h2 className="text-h4 text-chalk">Transcript</h2>
        <p className="mt-2 text-body text-chalk-dim">
          {status === 'pending'
            ? 'Still listening. Cuts work now; the words arrive in a minute or two.'
            : 'No transcript for this recording. Cuts still work from the timeline.'}
        </p>
      </div>
    );
  }

  const inSelection = (w: Word) => selection !== null && w.e > selection[0] && w.s < selection[1];

  return (
    <div className="select-none">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-h4 text-chalk">Transcript</h2>
        <span className="text-right text-caption text-chalk-faint">
          Drag to select · double-click a word · pencil edits a line
        </span>
      </div>
      <div className="mt-3 space-y-2">
        {lines.map((line, lineIndex) => {
          const lineWords = words.slice(line.from, line.to);
          const live = time >= lineWords[0].s && time < lineWords[lineWords.length - 1].e;
          if (editingLine === lineIndex) {
            return (
              <div key={lineIndex} className="rounded-md border border-chalk/30 bg-slate-900 p-2">
                <textarea
                  autoFocus
                  value={lineDraft}
                  rows={3}
                  onChange={(event) => setLineDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setEditingLine(null);
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      onReplaceSpan(line.from, line.to, retime(lineWords, lineDraft));
                      setEditingLine(null);
                    }
                  }}
                  className="w-full resize-none bg-transparent text-body text-chalk outline-none"
                />
                <div className="mt-1 flex items-center justify-between text-caption text-chalk-faint">
                  <span>
                    {timecode(lineWords[0].s)} to {timecode(lineWords[lineWords.length - 1].e)} ·
                    Enter saves, Esc cancels
                  </span>
                  <span>
                    <button
                      type="button"
                      className="hover:text-chalk"
                      onClick={() => setEditingLine(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ml-3 text-chalk hover:underline"
                      onClick={() => {
                        onReplaceSpan(line.from, line.to, retime(lineWords, lineDraft));
                        setEditingLine(null);
                      }}
                    >
                      Save
                    </button>
                  </span>
                </div>
              </div>
            );
          }
          return (
            <div
              key={lineIndex}
              className={cx('group flex gap-2 rounded-md px-1 py-0.5', live && 'bg-slate-900/60')}
            >
              <button
                type="button"
                onClick={() => onSeek(lineWords[0].s)}
                className="w-11 shrink-0 pt-1 text-left font-mono text-caption text-signal"
              >
                {timecode(lineWords[0].s)}
              </button>
              <p className="min-w-0 flex-1 leading-7 text-body">
                {lineWords.map((word, offset) => {
                  const index = line.from + offset;
                  const cut = isCut(word.s, keep);
                  const spoken = time >= word.s && time < word.e;
                  const selected = inSelection(word);
                  if (editing === index) {
                    return (
                      <input
                        key={index}
                        autoFocus
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onBlur={() => {
                          if (draft.trim() && draft.trim() !== word.w)
                            onChangeWord(index, draft.trim());
                          setEditing(null);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
                          if (event.key === 'Escape') setEditing(null);
                        }}
                        className="mx-0.5 inline-block w-28 rounded-xs border border-chalk/30 bg-slate-900 px-1 text-body text-chalk"
                      />
                    );
                  }
                  return (
                    <span
                      key={index}
                      onMouseDown={(event) => {
                        if (event.detail === 2) return;
                        anchor.current = index;
                        onSelect([word.s, word.e]);
                      }}
                      onMouseEnter={() => {
                        const from = anchor.current;
                        if (from === null) return;
                        const a = words[Math.min(from, index)];
                        const b = words[Math.max(from, index)];
                        onSelect([a.s, b.e]);
                      }}
                      onMouseUp={() => {
                        if (anchor.current === index) onSeek(word.s);
                        anchor.current = null;
                      }}
                      onDoubleClick={() => {
                        setDraft(word.w);
                        setEditing(index);
                      }}
                      className={cx(
                        'cursor-pointer rounded-xs px-0.5 transition-colors',
                        cut && 'text-chalk-faint line-through',
                        !cut &&
                          !selected &&
                          (spoken ? 'bg-slate-700 text-chalk' : 'text-chalk-dim hover:text-chalk'),
                        selected && 'bg-record/40 text-chalk',
                      )}
                    >
                      {word.w}{' '}
                    </span>
                  );
                })}
              </p>
              <button
                type="button"
                aria-label="Edit this line"
                title="Edit this line"
                onClick={() => {
                  setLineDraft(lineWords.map((w) => w.w).join(' '));
                  setEditingLine(lineIndex);
                }}
                className="shrink-0 self-start pt-1 text-caption text-chalk-faint opacity-0 transition-opacity hover:text-chalk group-hover:opacity-100"
              >
                ✎
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timeline: the whole original, cuts shaded, waveform behind, drag to select,
// handles on the selection's edges and on every cut's edges, zoom with the
// slider or pinch, scroll sideways when zoomed in.

function Timeline({
  duration,
  cuts,
  time,
  selection,
  sourceUrl,
  onSeek,
  onSelect,
  onDelete,
  onRestore,
  onMoveCutEdge,
  onSelectSegment,
}: {
  duration: number;
  cuts: Range[];
  time: number;
  selection: Range | null;
  sourceUrl: string;
  onSeek: (at: number) => void;
  onSelect: (range: Range | null) => void;
  onDelete: () => void;
  onRestore: (cut: Range) => void;
  onMoveCutEdge: (cut: Range, edge: 'start' | 'end', to: number) => void;
  onSelectSegment: (at: number) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [zoom, setZoom] = useState(1);
  const [peaks, setPeaks] = useState<Float32Array | null>(null);
  const [covered, setCovered] = useState(0);
  const [waveDone, setWaveDone] = useState(false);

  // What the mouse is doing: nothing, selecting, dragging a selection edge,
  // or dragging a cut's edge. A ref, so the move after the press sees it.
  const drag = useRef<
    | { kind: 'select'; start: number; moved: boolean }
    | { kind: 'selection-edge'; edge: 'start' | 'end'; other: number }
    | { kind: 'cut-edge'; cut: Range; edge: 'start' | 'end' }
    | null
  >(null);

  const timeAt = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    return Math.max(0, Math.min(duration, ((clientX - rect.left) / rect.width) * duration));
  };
  const left = (t: number) => `${(t / duration) * 100}%`;
  const width = (s: number, e: number) => `${((e - s) / duration) * 100}%`;

  useEffect(() => {
    const controller = new AbortController();
    setPeaks(null);
    setCovered(0);
    setWaveDone(false);
    loadWaveform(
      sourceUrl,
      duration,
      (progress) => {
        setPeaks(new Float32Array(progress.peaks));
        setCovered(progress.coveredSec);
        if (progress.done) setWaveDone(true);
      },
      controller.signal,
    ).catch(() => setWaveDone(true));
    return () => controller.abort();
  }, [sourceUrl, duration]);

  // Drawn at the zoomed width so bars stay one per bucket.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !peaks) return;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.floor(rect.width * devicePixelRatio));
    canvas.height = Math.max(1, Math.floor(rect.height * devicePixelRatio));
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'rgba(155, 167, 180, 0.55)';
    const mid = canvas.height / 2;
    const step = canvas.width / peaks.length;
    for (let i = 0; i < peaks.length; i += 1) {
      const h = Math.max(1, peaks[i] * canvas.height * 0.9);
      ctx.fillRect(i * step, mid - h / 2, Math.max(1, step - 0.5), h);
    }
  }, [peaks, zoom]);

  // Keep the playhead in view when zoomed in and playing.
  useEffect(() => {
    const scroller = scrollRef.current;
    const track = trackRef.current;
    if (!scroller || !track || zoom === 1) return;
    const x = (time / duration) * track.clientWidth;
    if (x < scroller.scrollLeft + 40 || x > scroller.scrollLeft + scroller.clientWidth - 40) {
      scroller.scrollLeft = Math.max(0, x - scroller.clientWidth / 3);
    }
  }, [time, zoom, duration]);

  const ticks = useMemo(() => {
    const visibleSec = duration / zoom;
    const step =
      visibleSec <= 30
        ? 1
        : visibleSec <= 90
          ? 5
          : visibleSec <= 300
            ? 15
            : visibleSec <= 900
              ? 60
              : visibleSec <= 3600
                ? 300
                : 600;
    const out: number[] = [];
    for (let t = 0; t <= duration; t += step) out.push(t);
    return out;
  }, [duration, zoom]);

  const onMove = (event: React.MouseEvent) => {
    const state = drag.current;
    if (!state) return;
    const at = timeAt(event.clientX);
    if (state.kind === 'select') {
      if (Math.abs(at - state.start) > duration / (400 * zoom)) {
        state.moved = true;
        onSelect([Math.min(state.start, at), Math.max(state.start, at)]);
      }
    } else if (state.kind === 'selection-edge') {
      onSelect([Math.min(state.other, at), Math.max(state.other, at)]);
    } else {
      onMoveCutEdge(state.cut, state.edge, at);
    }
  };

  const setZoomKeepingCentre = (next: number) => {
    const scroller = scrollRef.current;
    const clamped = Math.max(1, Math.min(40, next));
    if (scroller) {
      const centre =
        (scroller.scrollLeft + scroller.clientWidth / 2) / (scroller.clientWidth * zoom);
      setZoom(clamped);
      requestAnimationFrame(() => {
        scroller.scrollLeft = centre * scroller.clientWidth * clamped - scroller.clientWidth / 2;
      });
    } else {
      setZoom(clamped);
    }
  };

  const handle = (key: string, at: number, onDown: () => void, tone: 'selection' | 'cut') => (
    <div
      key={key}
      role="separator"
      aria-orientation="vertical"
      onMouseDown={(event) => {
        event.stopPropagation();
        onDown();
      }}
      className={cx(
        'absolute inset-y-0 z-10 w-2 -translate-x-1/2 cursor-col-resize',
        tone === 'selection' ? 'bg-record' : 'bg-chalk/70 hover:bg-chalk',
      )}
      style={{ left: left(at) }}
    >
      <div className="absolute left-1/2 top-1/2 h-5 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-950/60" />
    </div>
  );

  return (
    <div className="shrink-0 border-t border-hairline bg-slate-950 px-4 pb-4 pt-3">
      <div className="mb-2 flex items-center gap-4 text-caption">
        <button
          type="button"
          onClick={onDelete}
          disabled={!selection}
          className="text-chalk-dim disabled:opacity-40 hover:text-chalk"
        >
          Delete selection
          {selection ? ` (${timecode(selection[0])} to ${timecode(selection[1])})` : ''}
        </button>
        {selection ? (
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="text-chalk-faint hover:text-chalk"
          >
            Clear selection
          </button>
        ) : null}
        <span className="flex-1" />
        <span className="text-chalk-faint">
          {cuts.length === 0
            ? 'Nothing cut · click a piece to select it'
            : `${cuts.length} cut${cuts.length === 1 ? '' : 's'} · click a cut to restore it · drag its edges to trim`}
        </span>
        <label className="flex items-center gap-2 text-chalk-faint">
          <button
            type="button"
            onClick={() => setZoomKeepingCentre(1)}
            className={cx('hover:text-chalk', zoom === 1 && 'text-chalk')}
          >
            Fit
          </button>
          <input
            type="range"
            min={1}
            max={40}
            step={0.5}
            value={zoom}
            onChange={(event) => setZoomKeepingCentre(Number(event.target.value))}
            aria-label="Zoom"
            className="w-28 accent-chalk"
          />
        </label>
      </div>

      <div
        ref={scrollRef}
        className="overflow-x-auto overflow-y-hidden"
        onWheel={(event) => {
          if (!event.ctrlKey && !event.metaKey) return;
          event.preventDefault();
          setZoomKeepingCentre(zoom * (event.deltaY < 0 ? 1.15 : 1 / 1.15));
        }}
      >
        <div style={{ width: `${zoom * 100}%` }}>
          <div className="relative h-5 text-micro text-chalk-faint">
            {ticks.map((t) => (
              <span
                key={t}
                className="absolute -translate-x-1/2 font-mono"
                style={{ left: left(t) }}
              >
                {timecode(t)}
              </span>
            ))}
          </div>

          <div
            ref={trackRef}
            className="relative h-16 cursor-crosshair select-none overflow-hidden rounded-md border border-hairline bg-slate-900"
            onMouseDown={(event) => {
              const at = timeAt(event.clientX);
              drag.current = { kind: 'select', start: at, moved: false };
              onSelect(null);
              onSeek(at);
            }}
            onMouseMove={onMove}
            onMouseUp={() => {
              // A press with no drag selects the piece under it.
              const state = drag.current;
              if (state?.kind === 'select' && !state.moved) onSelectSegment(state.start);
              drag.current = null;
            }}
            onMouseLeave={() => {
              drag.current = null;
            }}
          >
            <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
            {!waveDone && covered < duration ? (
              <div
                className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-micro text-chalk-faint"
                style={{ left: left(covered) }}
              >
                <span className="ml-auto">{peaks ? 'reading audio' : 'reading audio…'}</span>
              </div>
            ) : null}
            {cuts.map((cut) => (
              <button
                key={`${cut[0]}-${cut[1]}`}
                type="button"
                title={`Restore ${timecode(cut[0])} to ${timecode(cut[1])}`}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  onRestore(cut);
                }}
                className="absolute inset-y-0 bg-slate-950/80 bg-[repeating-linear-gradient(135deg,transparent_0_6px,rgba(255,255,255,0.06)_6px_8px)] hover:bg-slate-950/60"
                style={{ left: left(cut[0]), width: width(cut[0], cut[1]) }}
              />
            ))}
            {cuts.flatMap((cut) => [
              cut[0] > 0.01
                ? handle(
                    `c${cut[0]}s`,
                    cut[0],
                    () => {
                      drag.current = { kind: 'cut-edge', cut, edge: 'start' };
                    },
                    'cut',
                  )
                : null,
              cut[1] < duration - 0.01
                ? handle(
                    `c${cut[1]}e`,
                    cut[1],
                    () => {
                      drag.current = { kind: 'cut-edge', cut, edge: 'end' };
                    },
                    'cut',
                  )
                : null,
            ])}
            {selection ? (
              <>
                <div
                  className="pointer-events-none absolute inset-y-0 bg-record/30"
                  style={{ left: left(selection[0]), width: width(selection[0], selection[1]) }}
                />
                {handle(
                  'ss',
                  selection[0],
                  () => {
                    drag.current = { kind: 'selection-edge', edge: 'start', other: selection[1] };
                  },
                  'selection',
                )}
                {handle(
                  'se',
                  selection[1],
                  () => {
                    drag.current = { kind: 'selection-edge', edge: 'end', other: selection[0] };
                  },
                  'selection',
                )}
              </>
            ) : null}
            <div
              className="pointer-events-none absolute inset-y-0 z-20 w-px bg-chalk"
              style={{ left: left(time) }}
            >
              <div className="absolute -left-1 -top-0.5 h-2 w-2 rotate-45 bg-chalk" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
